import { ICitation } from '@vivli/shared/features/citations/infrastructure/interface';
import { ColDef, GridApi } from 'ag-grid-community';
import { CSSProperties, useRef } from 'react';
import { ButtonView, GridView } from '@vivli/shared/views';
import { useCitationsContext } from '@vivli/shared/features/citations/infrastructure/context';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { useNavigate } from 'react-router';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { CitationView } from './citation.view';

const btnStyle: CSSProperties = {
    padding: 0,
    minWidth: '45px',
    width: '45px',
};

interface CitationsGridViewProps {
    onGridReady?: (gridApi: GridApi) => void;
    emptyMessage?: string;
    hideFloatingFilter?: boolean;
    hideHeader?: boolean;
    autoHeight?: boolean;
    asyncUpdateKey?: string;
    readonly?: boolean;
}

export const CitationsGridView = ({
    onGridReady,
    emptyMessage = 'No Citations Found.',
    hideFloatingFilter = true,
    hideHeader = true,
    autoHeight = true,
    readonly,
}: CitationsGridViewProps) => {
    const { citations, objectId, isStudy, showDisclosureLinks, removeCitation, updateCitation, permissions } = useCitationsContext();
    const gridApiRef = useRef<GridApi>();
    const navigate = useNavigate();

    const handleOnGridReady = (gridApi: GridApi) => {
        onGridReady && onGridReady(gridApi);
        gridApiRef.current = gridApi;
    };

    const handleDeleteCitation = (citation: ICitation) => {
        removeCitation(citation.id);
    };

    const columnDefs: ColDef<ICitation>[] = [
        {
            headerName: 'Citation',
            filter: true,
            sortable: true,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => ({
                component: <CitationView citation={data} />,
            }),
        },
        {
            hide: !showDisclosureLinks,
            valueGetter: ({ data }) => (data?.disclosureId ? 'View Disclosure' : ''),
            maxWidth: 150,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `citation-row-${node.rowIndex}-disclosure-view-button`,
                allowCopy: false,
                showPaperclipIcon: false,
                onClick: () => navigate(`/disclosure/${objectId}/${data.disclosureId}`),
            }),
        },
        {
            hide: !permissions.canDeleteCitation,
            maxWidth: 115,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data, node }) => {
                const canModify = permissions.canModifyCitation && !isStudy && !readonly && data.doi?.length <= 0;

                const btnContainerStyle: CSSProperties = {
                    display: 'grid',
                    gridTemplateColumns: canModify ? 'auto auto' : 'auto',
                    columnGap: '10px',
                    justifyContent: 'end',
                };

                return {
                    component: (
                        <div style={btnContainerStyle}>
                            {canModify && !readonly && (
                                <ButtonView style={btnStyle} theme={'blue-on-white'} faIcon={faEdit} onClick={() => updateCitation(data)} />
                            )}
                            <ButtonView
                                faIcon={faTrash}
                                theme={'red-on-white'}
                                style={btnStyle}
                                onClick={() => handleDeleteCitation(data)}
                            />
                        </div>
                    ),
                    dataId: `citation-row-${node.rowIndex}-delete-btn-container`,
                };
            },
        },
    ];

    return (
        <GridView
            rowData={citations}
            fullWidthColumns={true}
            onGridReady={handleOnGridReady}
            columns={columnDefs}
            emptyMessage={emptyMessage}
            hideFloatingFilter={hideFloatingFilter}
            hideHeader={hideHeader}
            autoHeight={autoHeight}
            rowHeight={85}
        />
    );
};
